import React, { useState, useEffect } from 'react';
import { API_URL } from './constants';

function CheckoutScreen({
  itemType,
  itemName,
  itemDetails,
  invoiceId,
  originalPrice,
  centerId,
  onPayNow,
  onBack,
  paymentLoading,
  hideButtons,
}) {
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [couponApplied, setCouponApplied] = useState('');
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!invoiceId) return;
    fetch(`${API_URL}/api/invoice/${invoiceId}/details`)
      .then(r => r.json())
      .then(data => {
        if (data.success) setInvoice(data);
        else setError('Could not load order details.');
      })
      .catch(() => setError('Could not load order details.'))
      .finally(() => setLoading(false));
  }, [invoiceId]);

  async function handleApplyCoupon() {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    setCouponError('');
    setCouponApplied('');
    try {
      const res = await fetch(`${API_URL}/api/invoice/apply-coupon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoiceId, couponCode: couponCode.trim().toUpperCase(), centerId })
      });
      const data = await res.json();
      if (data.success) {
        setCouponApplied(data.message || 'Discount applied!');
        setInvoice(prev => ({ ...prev, subtotal: data.subtotal, discount: data.discount, tax: data.tax, total: data.total }));
      } else {
        setCouponError(data.error || 'Could not apply discount code.');
      }
    } catch {
      setCouponError('Something went wrong. Please try again.');
    } finally {
      setCouponLoading(false);
    }
  }

  const subtotal = invoice?.subtotal ?? originalPrice ?? 0;
  const discount = invoice?.discount ?? 0;
  const tax = invoice?.tax ?? 0;
  const total = invoice?.total ?? originalPrice ?? 0;

  return (
    <div className={hideButtons ? "checkout-wrapper" : "step-content"}>
      {!hideButtons && (
        <>
          <h1 className="step-title">CHECKOUT</h1>
          <p className="step-subtitle">Review your order</p>
        </>
      )}
      {error && <div className="error-banner">{error}</div>}

      {loading ? (
        <div className="loading-spinner" style={{ margin: '60px auto' }}></div>
      ) : (
        <div className="checkout-card">
          {/* Summary Table */}
          <div className="checkout-table">
            <div className="checkout-table-header">
              <span>DESCRIPTION</span>
              <span>AMOUNT</span>
            </div>
            <div className="checkout-table-row">
              <span>{itemName}</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>

            {/* Extra details */}
            {itemDetails?.map((d, i) => (
              <div key={i} className="checkout-detail-row">
                <span className="checkout-detail-label">{d.label}</span>
                <span className="checkout-detail-value">{d.value}</span>
              </div>
            ))}
          </div>

          {/* Coupon Code */}
          <div className="checkout-coupon-section">
            <div className="checkout-coupon-label">Coupon code</div>
            <div className="checkout-coupon-row">
              <input
                className="auth-input"
                type="text"
                placeholder="Enter code"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon()}
                disabled={couponLoading || !!couponApplied}
                style={{ textTransform: 'uppercase', letterSpacing: '1px' }}
              />
              <button
                className="checkout-apply-btn"
                onClick={handleApplyCoupon}
                disabled={couponLoading || !couponCode.trim() || !!couponApplied}
              >
                {couponLoading ? '...' : 'APPLY'}
              </button>
            </div>
            {couponApplied && <div className="checkout-coupon-success">{couponApplied}</div>}
            {couponError && <div className="checkout-coupon-error">{couponError}</div>}
          </div>

          {/* Totals */}
          <div className="checkout-totals">
            <div className="checkout-totals-row">
              <span>Sub-Total</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>
            {discount > 0 && (
              <div className="checkout-totals-row checkout-discount">
                <span>Discount</span>
                <span>-${discount.toFixed(2)}</span>
              </div>
            )}
            <div className="checkout-totals-row">
              <span>Tax</span>
              <span>${tax.toFixed(2)}</span>
            </div>
            <div className="checkout-totals-row checkout-total-final">
              <span>Total</span>
              <span>${total.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}

      {!hideButtons && (
        <>
          <button
            className="auth-button"
            style={{ marginTop: '24px', width: '100%' }}
            onClick={() => onPayNow({ invoiceId, finalTotal: total })}
            disabled={paymentLoading || loading}
          >
            {paymentLoading ? 'OPENING PAYMENT...' : `PAY NOW`}
          </button>

          <button
            className="auth-button"
            style={{ marginTop: '12px', width: '100%', background: 'transparent', color: '#999', border: '1.5px solid #ddd' }}
            onClick={onBack}
            disabled={paymentLoading}
          >
            BACK
          </button>
        </>
      )}
    </div>
  );
}

export default CheckoutScreen;
